import { ROUTES } from "@config/app.routing";
import { FeatureTogglesType } from "@config/featureToggles";
import { defaultValues } from "@config/defaultValues";

import {
  EXTRA_SERVICES_BOOKING,
  EXTRA_SERVICES_LEGACY,
  AGENT_PREFERENCE,
  CLEANING_FREQUENCY,
} from "@typings/booking";
import { AvailabilitiesRequest, Task } from "@typings/bookingAvailabilities";
import { PAYMENT_METHODS } from "@typings/globals";

import { generateReturnUrl } from "@services/ProvidePaymentUrls";

import { ContactUsForBookingFormData } from "@app/pages/Booking/modals/ContactUsForBooking.helpers";
import { IStore } from "@app/redux/reducers";

import { getServiceLocationInfo } from "../serviceLocation/serviceLocation.selectors";

import { CheckoutParams } from "./booking.typings";
import {
  getSelectedTasksList,
  getSelectedCleaningFrequency,
  getServiceLocationId,
  getAgentPreference,
  getHasPets,
  getRoomQuantity,
  getVoucher,
  getPreselectedAgentUuid,
  getReservationUuid,
  getClientRegistrationForm,
  getKeyPick,
  getLocationComment,
  getSpecialInstructions,
  getLocationUuid,
  getSelectedPaymentMethod,
  getTemplateJobId,
  getSelectedPricePlanUuid,
  getSelectedCleaningProductsIds,
  getContactAfterBooking,
  getNonStandardFrequencyShifts,
  getHasServiceVouchers,
  getTotalDurationOfMoreOftenFrequencyShifts,
  getPetsDetails,
  getIsCleaningChecklistAvailable,
  getExecution,
  getExecutionDate,
} from "./booking.selectors";
import {
  prepareTaskFactory,
  transformNonStandardToMoreOftenFrequencyFactory,
  transformTaskServiceFactory,
} from "./booking.factories";

import { getHasUrssaf } from ".";

//@ToDo: remove this condition once backend fixes extra services enum
// "Micheal Owen gate"
const ovenToOwenInstruction = {
  toReplace: EXTRA_SERVICES_BOOKING.INSIDE_OVEN,
  replaceBy: EXTRA_SERVICES_LEGACY.INSIDE_OWEN,
};

export const targetRoutes = {
  default: ROUTES.CLIENT_DASHBOARD,
  myCleanings: `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`,
  payFirst: `${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_BOOKINGS_TO_VALIDATE}`,
  urssaf: ROUTES.URSSAF_REGISTRATION,
  checklist: ROUTES.BOOKING_THANK_YOU,
};

export const getPricingEndpointRequest = (
  store: IStore,
  serviceLocationId: number,
  featureToggles?: FeatureTogglesType,
) => {
  const tasksData = getSelectedTasksList(store);
  const templateJobId = getTemplateJobId(store);
  const cleaningProductsIds = getSelectedCleaningProductsIds(store);
  const frequency = getSelectedCleaningFrequency(store);
  const moreOftenFrequencyTimeSlots = getNonStandardFrequencyShifts(store).map(
    transformNonStandardToMoreOftenFrequencyFactory,
  );
  const tasks = tasksData
    .map(
      prepareTaskFactory(tasksData, [
        {
          flag: getHasServiceVouchers(store) as boolean,
          hasService: EXTRA_SERVICES_BOOKING.IRONING,
          targetService: EXTRA_SERVICES_BOOKING.HOME_CLEANING,
          subtract: defaultValues.booking.tasks.IRONING.duration,
        },
        {
          flag: getHasServiceVouchers(store) as boolean,
          hasService: EXTRA_SERVICES_BOOKING.IRONING,
          targetService: EXTRA_SERVICES_BOOKING.IRONING,
          newValue: defaultValues.booking.tasks.IRONING.duration,
        },
      ]),
    )
    .map(transformTaskServiceFactory([ovenToOwenInstruction]))
    .map(task => {
      if (
        frequency === CLEANING_FREQUENCY.MORE_OFTEN &&
        task.service === EXTRA_SERVICES_BOOKING.HOME_CLEANING
      ) {
        return {
          ...task,
          duration: 0, // when More Often is chosen, we don't need to accumulate shifts, but just send 0
        };
      }
      return task;
    });

  return {
    serviceLocationId,
    pricePlanUuid: getSelectedPricePlanUuid(store),
    executionDateTime: getExecutionDate(store),
    voucherToken: getVoucher(store),
    tasks,
    frequency,
    ...(templateJobId && { templateJobId }),
    ...(!!featureToggles?.more_often_frequency_for_b2c &&
      frequency === CLEANING_FREQUENCY.MORE_OFTEN && {
        moreOftenFrequencyTimeSlots,
      }),
    ...(!!featureToggles?.cleaning_products_sale && {
      products: cleaningProductsIds
        .map(productId => ({ productId, quantity: 1 }))
        .toArray(),
    }),
  };
};

export const getAvailabilitiesBodyRequest = (
  store: IStore,
  serviceLocationId: number,
): AvailabilitiesRequest => {
  const tasksData = getSelectedTasksList(store);
  const tasks: Task[] = tasksData
    .map(
      prepareTaskFactory(tasksData, [
        {
          flag: getHasServiceVouchers(store) as boolean,
          hasService: EXTRA_SERVICES_BOOKING.IRONING,
          targetService: EXTRA_SERVICES_BOOKING.HOME_CLEANING,
          subtract: defaultValues.booking.tasks.IRONING.duration,
        },
        {
          flag: getHasServiceVouchers(store) as boolean,
          hasService: EXTRA_SERVICES_BOOKING.IRONING,
          targetService: EXTRA_SERVICES_BOOKING.IRONING,
          newValue: defaultValues.booking.tasks.IRONING.duration,
        },
      ]),
    )
    .map(transformTaskServiceFactory([ovenToOwenInstruction]))
    .toJS();
  const agentPreference = getAgentPreference(store);
  const reservationUuid = getReservationUuid(store);
  const preselectedAgentUuid = getPreselectedAgentUuid(store);
  const useUrssaf = getHasUrssaf(store);
  const pricePlanUuid = getSelectedPricePlanUuid(store);
  const petsDetails = getPetsDetails(store);

  return {
    serviceLocationId,
    frequency: getSelectedCleaningFrequency(store),
    hasPets: getHasPets(store),
    pets: petsDetails.get("types"),
    petsDescription: petsDetails.get("description"),
    agentPreference,
    pricePlanUuid,
    tasks,
    ...(reservationUuid && { reservationUuid }),
    ...(preselectedAgentUuid &&
      agentPreference === AGENT_PREFERENCE.PRESELECTED && {
        preselectedAgentUuid,
      }),
    ...(useUrssaf && { useUrssaf }),
  };
};

export const getReservationEndpointRequest = (
  store: IStore,
  featureToggles?: FeatureTogglesType,
) => {
  const execution = getExecution(store);
  const executionDate = getExecutionDate(store);
  const tasksData = getSelectedTasksList(store);
  const agentPreference = getAgentPreference(store);
  const preselectedAgentUuid = getPreselectedAgentUuid(store);
  const frequency = getSelectedCleaningFrequency(store);
  const cleaningProductsIds = getSelectedCleaningProductsIds(store);
  const useUrssaf = getHasUrssaf(store);
  const petsDetails = getPetsDetails(store);
  const bedrooms = getRoomQuantity(store, "bedrooms");
  const bathrooms = getRoomQuantity(store, "bathrooms");
  const moreOftenFrequencyTimeSlots = getNonStandardFrequencyShifts(store).map(
    transformNonStandardToMoreOftenFrequencyFactory,
  );
  const moreOftenFrequencyContractStartDate = execution.date;
  const totalDurationOfMoreOftenCleanings =
    getTotalDurationOfMoreOftenFrequencyShifts(store);
  const tasks = tasksData
    .map(
      prepareTaskFactory(tasksData, [
        {
          flag: getHasServiceVouchers(store) as boolean,
          hasService: EXTRA_SERVICES_BOOKING.IRONING,
          targetService: EXTRA_SERVICES_BOOKING.HOME_CLEANING,
          subtract: defaultValues.booking.tasks.IRONING.duration,
        },
        {
          flag: getHasServiceVouchers(store) as boolean,
          hasService: EXTRA_SERVICES_BOOKING.IRONING,
          targetService: EXTRA_SERVICES_BOOKING.IRONING,
          newValue: defaultValues.booking.tasks.IRONING.duration,
        },
      ]),
    )
    .map(transformTaskServiceFactory([ovenToOwenInstruction]))
    .map(task => {
      if (
        frequency === CLEANING_FREQUENCY.MORE_OFTEN &&
        task.service === EXTRA_SERVICES_BOOKING.HOME_CLEANING
      ) {
        return {
          ...task,
          duration: totalDurationOfMoreOftenCleanings,
        };
      }
      return task;
    });

  return {
    serviceLocationId: getServiceLocationId(store),
    executionDateTime: executionDate,
    hasPets: getHasPets(store),
    pets: petsDetails.get("types"),
    petsDescription: petsDetails.get("description"),
    pricePlanUuid: getSelectedPricePlanUuid(store),
    bedrooms,
    bathrooms,
    agentPreference,
    frequency,
    tasks,
    contactAfterBooking: getContactAfterBooking(store),
    ...(preselectedAgentUuid &&
      agentPreference === AGENT_PREFERENCE.PRESELECTED && {
        preselectedAgentUuid,
      }),
    ...(!!featureToggles?.more_often_frequency_for_b2c &&
      frequency === CLEANING_FREQUENCY.MORE_OFTEN && {
        moreOftenFrequencyTimeSlots,
        moreOftenFrequencyContractStartDate,
      }),
    ...(!!featureToggles?.cleaning_products_sale && {
      products: cleaningProductsIds
        .map(productId => ({ productId, quantity: 1 }))
        .toArray(),
    }),
    ...(useUrssaf && { useUrssaf }),
  };
};

export const getClientRegistrationEndpointRequest = (store: IStore) => {
  const form = getClientRegistrationForm(store);
  const petsDetails = getPetsDetails(store);
  const selectedPaymentMethod = getSelectedPaymentMethod(store);

  const serviceVoucher = form.get("serviceVoucher");

  return {
    email: form.get("email"),
    password: form.get("password"),
    firstName: form.get("firstName"),
    lastName: form.get("lastName"),
    phoneNumber: form.get("phoneNumber"),
    mainLocation: {
      serviceLocationId: getServiceLocationId(store),
      address: form.get("address"),
      floorAndDoor: form.get("floorAndDoor"),
      entryCode: form.get("entryCode"),
      hasPets: getHasPets(store),
      pets: petsDetails.get("types"),
      petsDescription: petsDetails.get("description"),
    },
    marketingConsent: form.get("marketingConsent"),
    invoicePayment: selectedPaymentMethod === PAYMENT_METHODS.MONTHLY_INVOICE,
    ...(serviceVoucher && { serviceVoucher }),
  };
};

export const getAddLocationEndpointRequest = (store: IStore) => {
  const form = getClientRegistrationForm(store);
  const petsDetails = getPetsDetails(store);

  return {
    serviceLocationId: getServiceLocationId(store),
    address: form.get("address"),
    floorAndDoor: form.get("floorAndDoor"),
    entryCode: form.get("entryCode"),
    hasPets: getHasPets(store),
    pets: petsDetails.get("types"),
    petsDescription: petsDetails.get("description"),
  };
};

export const getCheckoutEndpointRequest = (
  store: IStore,
  params: CheckoutParams,
) => {
  const resolveTargetRoute = () => {
    if (params.targetRoute) {
      return params.targetRoute;
    }

    if (hasUrssaf) {
      return targetRoutes.urssaf;
    }

    if (
      params.featureToggles?.cleaning_checklist &&
      isReservationWithChecklist
    ) {
      return targetRoutes.checklist;
    }

    return targetRoutes.myCleanings;
  };

  const reservationUuid = getReservationUuid(store);
  const isReservationWithChecklist = getIsCleaningChecklistAvailable(store);
  const voucherToken = getVoucher(store);
  const templateJobId = getTemplateJobId(store);
  const hasUrssaf = getHasUrssaf(store);
  const targetRoute = resolveTargetRoute();
  const returnUrl = generateReturnUrl({
    originRoute: params.originRoute || ROUTES.BOOKING_CHECKOUT,
    targetRoute,
    options: {
      encodeTargetRoute: true,
    },
    reservationUuid,
  });

  return {
    returnUrl,
    keyPick: getKeyPick(store),
    locationComment: getLocationComment(store),
    specialInstructions: getSpecialInstructions(store),
    paymentMethod: getSelectedPaymentMethod(store),
    locationUuid: getLocationUuid(store),
    ...(voucherToken && { voucherToken }),
    ...(templateJobId && { templateJobId }),
  };
};

export const getSendBookingContactFormRequest = (
  store: IStore,
  form: ContactUsForBookingFormData,
) => {
  const execution = getExecution(store);
  const petsDetails = getPetsDetails(store);
  const tasks = getSelectedTasksList(store).map(item => ({
    //@ToDo: remove this condition once backend fixes extra services enum
    service:
      item.get("service") === EXTRA_SERVICES_BOOKING.INSIDE_OVEN
        ? EXTRA_SERVICES_LEGACY.INSIDE_OWEN
        : item.get("service"),
    duration: item.get("duration"),
  }));

  return {
    zipCode: getServiceLocationInfo(store).get("zip"),
    frequency: getSelectedCleaningFrequency(store),
    pricePlanId: getSelectedPricePlanUuid(store),
    tasks,
    hasPets: getHasPets(store),
    pets: petsDetails.get("types"),
    petsDescription: petsDetails.get("description"),
    selectedDate: execution.date,
    preferredDaysOfWeek: form.preferredDaysOfWeek,
    preferredDayTimes: form.preferredDayTimes,
    contact: form.contact,
    additionalInformation: form.additionalInformation,
  };
};
